import React, { useRef, useState } from 'react';
import { ImagePlus, Images, Loader2, Trash2, UploadCloud } from 'lucide-react';
import { toast } from 'sonner';

import { mediaAPI } from '../lib/api';
import CloudinaryMediaLibraryDialog from './CloudinaryMediaLibraryDialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';


const MEDIA_SLOTS = [
  { key: 'hero_background', label: 'Homepage hero', description: 'Full-screen background behind the homepage headline', width: 1920, height: 1080, safeArea: 'Keep the model/subject near the centre for mobile cropping.' },
  { key: 'collection_story', label: 'Collection story', description: 'Homepage collection story image', width: 1600, height: 1000 },
  { key: 'collections_banner', label: 'Collections page banner', description: 'Main banner for the storefront Collections page', width: 1920, height: 1080, safeArea: 'Keep important content away from the far-left text overlay.' },
  { key: 'fit_regular', label: 'Regular fit', description: 'Homepage regular-fit category image', width: 1600, height: 900 },
  { key: 'fit_oversized', label: 'Oversized fit', description: 'Homepage oversized-fit category image', width: 1600, height: 900 },
  { key: 'signature_product', label: 'Signature product', description: 'Homepage signature product image', width: 1600, height: 1000 },
  { key: 'campaign_primary', label: 'Campaign – main', description: 'Full-width homepage campaign image. The storefront displays the complete image without cropping.', width: 1600, height: 900 },
  { key: 'campaign_secondary_top', label: 'Campaign – top', description: 'Secondary campaign image displayed at its complete 4:3 ratio without cropping.', width: 1200, height: 900 },
  { key: 'campaign_secondary_bottom', label: 'Campaign – bottom', description: 'Secondary campaign image displayed at its complete 4:3 ratio without cropping.', width: 1200, height: 900 },
  { key: 'about_hero', label: 'About Us – Hero', description: 'Full-width background for the About Us page hero.', width: 1920, height: 1080, safeArea: 'Keep important subjects near the centre for responsive cropping.' },
  { key: 'page_primary', label: 'About Us – Who We Are', description: 'Image beside the Who We Are / Redefining Streetwear section.', width: 1200, height: 1500 },
  { key: 'page_secondary', label: 'About Us – Gaming, Anime & Urban Culture', description: 'Image beside the Gaming, anime & urban culture content.', width: 1200, height: 1500 },
  { key: 'contact_hero', label: 'Contact Us – Hero', description: 'Full-width background for the Contact Us page hero.', width: 1920, height: 1080, safeArea: 'Keep important subjects near the centre for responsive cropping.' },
  { key: 'contact_primary', label: 'Contact Us – Support Image', description: 'Primary image beside the contact information section.', width: 1200, height: 1500 },
  { key: 'contact_secondary', label: 'Contact Us – Community Image', description: 'Secondary image beside the FAQ/help section.', width: 1200, height: 1000 },
];

const previewUrl = (url) => url?.replace(
  '/image/upload/',
  '/image/upload/f_auto,q_auto:eco,c_limit,w_640/',
);

export default function StorefrontMediaManager({ media = {}, onChange, disabled = false, showHeader = true }) {
  const inputRef = useRef(null);
  const [activeSlot, setActiveSlot] = useState('');
  const [uploadingSlot, setUploadingSlot] = useState('');
  const [librarySlot, setLibrarySlot] = useState('');

  const chooseFile = (slot) => {
    setActiveSlot(slot);
    inputRef.current?.click();
  };

  const applyAsset = (slotKey, asset, message = 'selected') => {
    const slotInfo = MEDIA_SLOTS.find((item) => item.key === slotKey);
    onChange({
      ...media,
      [slotKey]: {
        ...asset,
        alt: asset?.alt || slotInfo?.label || 'URBNSW storefront image',
      },
    });
    if (slotInfo && asset?.width && asset?.height) {
      const actualRatio = asset.width / asset.height;
      const requiredRatio = slotInfo.width / slotInfo.height;
      const ratioDifference = Math.abs(actualRatio - requiredRatio) / requiredRatio;
      if (ratioDifference > 0.06 || asset.width < slotInfo.width || asset.height < slotInfo.height) {
        toast.warning(`${slotInfo.label}: use ${slotInfo.width} × ${slotInfo.height} px for the best display. Selected: ${asset.width} × ${asset.height} px.`);
      }
    }
    toast.success(`${slotInfo?.label || 'Storefront image'} ${message}`);
  };

  const uploadFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !activeSlot) return;
    setUploadingSlot(activeSlot);
    try {
      const asset = await mediaAPI.uploadFile(file);
      applyAsset(activeSlot, asset, 'uploaded');
    } catch (error) {
      toast.error(error.response?.data?.error?.message || error.message || 'Image upload failed');
    } finally {
      setUploadingSlot('');
      setActiveSlot('');
    }
  };

  const updateAlt = (slot, alt) => onChange({
    ...media,
    [slot]: { ...media[slot], alt },
  });

  const remove = (slot) => {
    const next = { ...media };
    delete next[slot];
    onChange(next);
  };

  return (
    <div className="space-y-4">
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/avif" onChange={uploadFile} className="hidden" />
      {showHeader && (
        <div>
          <h3 className="font-medium text-slate-900">Storefront images</h3>
          <p className="text-sm text-slate-500">Upload your own website images. They are stored in Cloudinary and delivered through its CDN.</p>
        </div>
      )}
      <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-xs leading-5 text-blue-900">
        <strong>Upload guide:</strong> use the exact resolution printed on each card. JPG/WebP is recommended for photos. Campaign and About Us artwork is displayed in full; only slots that mention a safe area may crop responsively.
      </div>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {MEDIA_SLOTS.map((slotInfo) => {
          const { key: slot, label, description, width, height, safeArea } = slotInfo;
          const asset = media[slot];
          const uploading = uploadingSlot === slot;
          return (
            <div key={slot} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="relative overflow-hidden rounded-md bg-white" style={{ aspectRatio: `${width} / ${height}` }}>
                {asset?.url ? (
                  <img src={previewUrl(asset.url)} alt={asset.alt || label} className="h-full w-full object-contain" />
                ) : (
                  <div className="flex h-full flex-col items-center justify-center text-slate-400">
                    <ImagePlus className="mb-2 h-7 w-7" />
                    <span className="text-xs">No image</span>
                  </div>
                )}
              </div>
              <div className="mt-3">
                <p className="text-sm font-medium text-slate-900">{label}</p>
                <p className="min-h-8 text-xs text-slate-500">{description}</p>
                <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-2 text-[11px] leading-4 text-emerald-800">
                  <strong>Required:</strong> {width} × {height} px · {Math.round((width / height) * 100) / 100}:1
                  {safeArea ? <span className="mt-1 block text-emerald-700">{safeArea}</span> : null}
                </div>
              </div>
              {asset?.url ? (
                <div className="mt-3 space-y-2">
                  <Label className="text-xs">Alt text</Label>
                  <Input value={asset.alt || ''} onChange={(event) => updateAlt(slot, event.target.value)} disabled={disabled} className="h-8 text-xs" />
                </div>
              ) : null}
              <div className="mt-3 flex flex-wrap gap-2">
                <Button type="button" size="sm" variant="outline" onClick={() => setLibrarySlot(slot)} disabled={disabled || uploading} className="flex-1">
                  <Images /> Choose existing
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={() => chooseFile(slot)} disabled={disabled || uploading} className="flex-1">
                  {uploading ? <Loader2 className="animate-spin" /> : <UploadCloud />}
                  {asset?.url ? 'Upload replacement' : 'Upload new'}
                </Button>
                {asset?.url ? <Button type="button" size="icon" variant="outline" onClick={() => remove(slot)} disabled={disabled} className="text-red-600"><Trash2 /></Button> : null}
              </div>
            </div>
          );
        })}
      </div>

      <CloudinaryMediaLibraryDialog
        open={Boolean(librarySlot)}
        onOpenChange={(open) => { if (!open) setLibrarySlot(''); }}
        title="Choose storefront image from Cloudinary"
        onSelect={(asset) => {
          applyAsset(librarySlot, asset, 'selected from Cloudinary');
          setLibrarySlot('');
        }}
      />
    </div>
  );
}
